import {
  StyledCard,
  StyledInput
} from '@bo-components/common/StyledComponents';
import { DEFAULT_GAME_TYPE_DATA } from '@bo-constants/gameTypes';
import { FormActionButtons } from '@library/components/buttons';
import type { GameTypeData } from '@library/types/gameTypes';
import StyledModal from '@bo-components/common/StyledModal';
import { selectHasEditGameTypes } from '@bo-selectors/utilsDashes';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import useUtilDetailsModal from '@bo-hooks/utilsDashboards/useUtilDetailsModal';

export interface GameTypeDetailsModalProps {
  isNewItem: boolean;
  isOpen: boolean;
  modalData: GameTypeData;
  onClose: () => void;
  onSubmit: (formData: Partial<GameTypeData>) => void;
  submitMessage: string;
}

export const GameTypeDetailsModal = ({
  isNewItem,
  isOpen,
  modalData,
  onClose,
  onSubmit,
  submitMessage
}: GameTypeDetailsModalProps) => {
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<GameTypeData>({
    mode: 'onChange'
  });

  const {
    formData,
    setFormData,
    isReadOnly,
    setIsReadOnly,
    onClickEdit,
    handleCancel
  } = useUtilDetailsModal({
    isNewItem,
    defaultItemData: DEFAULT_GAME_TYPE_DATA,
    modalData,
    onClose,
    reset
  });

  useEffect(() => {
    if (isNewItem) {
      setIsReadOnly(false);
    }
  }, [isNewItem, setIsReadOnly]);

  useEffect(() => {
    if (modalData) {
      setFormData(modalData);
    }
  }, [modalData, setFormData]);

  useEffect(() => {
    reset(formData);
  }, [reset, formData]);

  const isEditMode = !isReadOnly || isNewItem;

  const hasCreateOrEditGameTypes = useSelector(selectHasEditGameTypes);

  return (
    <StyledModal
      isEditAllowed={hasCreateOrEditGameTypes}
      isOpen={isOpen}
      onClose={onClose}
      onEdit={isNewItem ? undefined : onClickEdit}
      testId="stat-type-details-modal"
      bigTitle={formData.name}
      smallTitle={isEditMode ? 'Edit Game Type' : 'View Game Type'}
    >
      <form>
        <div className="flex gap-4">
          <StyledCard title="General Information">
            <StyledInput
              error={errors.name?.message}
              label="Name"
              name="name"
              readOnly={isReadOnly}
              register={register}
              required={isEditMode}
            />
            <StyledInput
              label="Description"
              name="description"
              readOnly={isReadOnly}
              register={register}
            />
          </StyledCard>
        </div>
      </form>

      {isEditMode && (
        <div className="border-soFresh-140 bg-soFresh-100 sticky bottom-0 flex items-center justify-end gap-4 border-t border-solid py-3 pr-5">
          {submitMessage ? (
            <p>{submitMessage}</p>
          ) : (
            <FormActionButtons
              className="flex items-center justify-end gap-4 border-none py-3 pr-5"
              primary={{
                onClick: handleSubmit(onSubmit),
                disabled: !isValid
              }}
              secondary={{ onClick: handleCancel }}
            />
          )}
        </div>
      )}
    </StyledModal>
  );
};
